import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HandHeart } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [prayer, setPrayer] = useState({ name: "", email: "", request: "" });
  const [praying, setPraying] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch { toast.error("Could not send. Please retry."); }
    finally { setSending(false); }
  };

  const submitPrayer = async (e) => {
    e.preventDefault();
    if (prayer.request.trim().length < 3) { toast.error("Please share your request"); return; }
    setPraying(true);
    try {
      const payload = { request: prayer.request };
      if (prayer.name.trim()) payload.name = prayer.name.trim();
      if (prayer.email.trim()) payload.email = prayer.email.trim();
      await api.post("/prayer-requests", payload);
      toast.success("Prayer received. You're not alone.");
      setPrayer({ name: "", email: "", request: "" });
    } catch { toast.error("Could not send. Please retry."); }
    finally { setPraying(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
      <p className="eyebrow mb-3">Contact</p>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight font-light">Say hello.</h1>
      <p className="mt-4 text-neutral-500 max-w-md">
        Questions about a song, a licence, or a technical hiccup? Send a note and we'll respond within a day.
      </p>

      <form onSubmit={submit} className="mt-12 space-y-6 max-w-xl">
        <div>
          <label className="eyebrow block mb-2">Name</label>
          <input
            data-testid="contact-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="w-full h-12 px-4 rounded-xl bg-white border border-[--sanctum-border] focus:outline-none focus:border-neutral-400"
          />
        </div>
        <div>
          <label className="eyebrow block mb-2">Email</label>
          <input
            data-testid="contact-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            className="w-full h-12 px-4 rounded-xl bg-white border border-[--sanctum-border] focus:outline-none focus:border-neutral-400"
          />
        </div>
        <div>
          <label className="eyebrow block mb-2">Message</label>
          <textarea
            data-testid="contact-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            required
            rows={6}
            className="w-full p-4 rounded-xl bg-white border border-[--sanctum-border] focus:outline-none focus:border-neutral-400 resize-none"
          />
        </div>
        <button
          data-testid="contact-submit"
          disabled={sending}
          className="btn-pill btn-primary disabled:opacity-60"
        >
          {sending ? "Sending…" : "Send message"}
        </button>
      </form>

      {/* Prayer requests */}
      <div className="mt-24 pt-16 border-t border-[--sanctum-border] max-w-xl">
        <div className="flex items-center gap-2 mb-3">
          <HandHeart size={16} strokeWidth={1.5} className="text-[--sanctum-accent]" />
          <p className="eyebrow">Private prayer inbox</p>
        </div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight font-light">Leave a request.</h2>
        <p className="mt-3 text-neutral-500 text-sm leading-relaxed">
          Anything you share here is read only by the artist. Name and email are optional — the burden is what matters.
        </p>

        <form onSubmit={submitPrayer} className="mt-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">Name (optional)</label>
              <input
                data-testid="prayer-name"
                value={prayer.name}
                onChange={(e) => setPrayer({ ...prayer, name: e.target.value })}
                className="w-full h-11 px-3 rounded-xl bg-white border border-[--sanctum-border] focus:outline-none focus:border-neutral-400 text-sm"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Email (optional)</label>
              <input
                data-testid="prayer-email"
                type="email"
                value={prayer.email}
                onChange={(e) => setPrayer({ ...prayer, email: e.target.value })}
                className="w-full h-11 px-3 rounded-xl bg-white border border-[--sanctum-border] focus:outline-none focus:border-neutral-400 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Your request</label>
            <textarea
              data-testid="prayer-request"
              value={prayer.request}
              onChange={(e) => setPrayer({ ...prayer, request: e.target.value })}
              required rows={5}
              placeholder="Whatever is heavy today…"
              className="w-full p-4 rounded-xl bg-white border border-[--sanctum-border] focus:outline-none focus:border-neutral-400 resize-none text-sm leading-relaxed"
            />
          </div>
          <button
            data-testid="prayer-submit"
            disabled={praying}
            className="btn-pill btn-primary disabled:opacity-60"
          >
            {praying ? "Sending…" : "Send prayer request"}
          </button>
        </form>
      </div>
    </div>
  );
}
